import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import type { Position2D } from '@Components/BigBrowse/Model/ModelTypes';
import { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import type { ViewData } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.data';
import { pipeAsyncTransformer } from '@Utils/pipeAsync';
import { pipeWith } from 'ramda';
import { HeaderBackgroundView } from './MultiSectionView.types';

export const FILTER_OUT_GAPS = true;

function backgroundMarkerFinder(id?: number) {
  return (card: GridCard<PlaneTypes>) =>
    card.type === PlaneTypes.Section_Background_Marker &&
    card.data?.sectionId === id;
}

function backgroundHeightUpdater(viewData: ViewData) {
  return (backgroundCard: GridCard<PlaneTypes.Section_Background>) => {
    const markerCard = viewData.layoutTable?.[0]?.find(
      backgroundMarkerFinder(backgroundCard.data?.id)
    );

    const height = markerCard?.topLeft.y
      ? backgroundCard.topLeft.y - markerCard!.topLeft!.y
      : 0; // if not markerCard found something is wrong

    backgroundCard.setSize({
      width: viewData.cameraProps.size.width!,
      height,
    });
  };
}

function headerBackgroundHeightUpdater(viewData: ViewData) {
  return (backgroundCard: GridCard<PlaneTypes.Header_Background>) => {
    const markerCard = viewData.layoutTable?.[0]?.find(
      (card) =>
        card.type === PlaneTypes.Header_Background_Marker &&
        (card.data as HeaderBackgroundView)?.concessionId ===
          backgroundCard.data?.concessionId
    );

    const height = markerCard?.topLeft.y
      ? backgroundCard.topLeft.y - markerCard!.topLeft!.y
      : 0; // if not markerCard found something is wrong

    backgroundCard.setSize({
      width: viewData.cameraProps.size.width!,
      height,
    });
  };
}

async function calculateBackgroundSizes(viewData: ViewData): Promise<ViewData> {
  viewData.layoutTable?.[0]
    ?.filter((card) => card.type === PlaneTypes.Section_Background)
    // @ts-expect-error // TODO: fix the type. filter only returns GridCard<PlaneTypes.Section_Background> items
    .forEach(backgroundHeightUpdater(viewData));

  viewData.layoutTable?.[0]
    ?.filter((card) => card.type === PlaneTypes.Header_Background)
    ?.forEach((item) =>
      headerBackgroundHeightUpdater(viewData)(
        item as GridCard<PlaneTypes.Header_Background>
      )
    );
  return viewData;
}

export const postPopulationProcess = pipeWith(pipeAsyncTransformer, [
  addPositions,
  calculateBackgroundSizes,
  removeGapsConditionally,
  removeMarkers,
  reCenterToLayoutCenter,
]) as (viewData: ViewData & { height?: number }) => Promise<ViewData>;

async function removeGapsConditionally(viewData: ViewData): Promise<ViewData> {
  const layoutTable = FILTER_OUT_GAPS
    ? removeGaps(viewData.layoutTable!)
    : viewData.layoutTable;

  return { ...viewData, layoutTable };
}

async function removeMarkers(viewData: ViewData): Promise<ViewData> {
  const layoutTable = viewData.layoutTable!.map((column) =>
    column.filter(
      (card) =>
        card.type !== PlaneTypes.Section_Background_Marker &&
        card.type !== PlaneTypes.Header_Background_Marker
    )
  );

  return { ...viewData, layoutTable };
}

async function addPositions(viewData: ViewData): Promise<ViewData> {
  let minTop = 0; // will track the lowest Y position of the layout.

  viewData.layoutTable!.forEach((column, columnIndex) => {
    const columnLeft =
      columnIndex * (viewData.columnWidth! + viewData.columnGap!); // Assuming TopLeft of mainColumn is (0,0).

    const columnTop = assignYPositionForColumn(column, columnLeft); // assuming TopLeft of mainColumn is (0,0).
    minTop = Math.min(minTop, columnTop); // minTop will help us to calculate the height of the layout.
  });

  return { ...viewData, layoutHeight: Math.abs(minTop) };
}

function assignYPositionForColumn(
  column: GridCard<PlaneTypes>[],
  columnLeft: number
): number {
  let top = 0;

  column.forEach((card) => {
    if (card.type === PlaneTypes.Section_Background) {
      // If card is a section_background card, it should be placed behind the items,
      // section_background card comes right before the section items
      // so not changing the y (top) coordinate
      card.setPosition({ x: 0, y: top });
    } else {
      card.setPosition({ x: columnLeft, y: top });
      top -= card.size.height; // Y column is bottom -> top
    }
  });

  return top; // return the minTop of the column
}

async function reCenterToLayoutCenter(viewData: ViewData): Promise<ViewData> {
  const { layoutTable, layoutWidth, layoutHeight, cameraProps } = viewData;

  // anchor point of card is at the center of the card.
  // anchor point of layout is at the center of the layout.
  layoutTable?.forEach((column) => {
    column.forEach((card) => {
      card.setPosition({
        x: -layoutWidth! / 2 + card.position.x + card.size.width / 2,
        y: Number(layoutHeight!) / 2 + card.position.y - card.size.height / 2,
      });
    });
  });

  // position  the layout so its top part is displayed in current screen window
  const layoutCenterWorldPosition: Position2D = {
    x: cameraProps.position.x,
    y: cameraProps.box.top - layoutHeight! / 2,
  };

  layoutTable?.flat().forEach((card) => {
    card.setParent({ worldPosition: layoutCenterWorldPosition });
  });

  return viewData;
}

function removeGaps(
  layoutTable: GridCard<PlaneTypes>[][]
): GridCard<PlaneTypes>[][] {
  return layoutTable.map((column) =>
    column.filter((card) => card.type !== PlaneTypes.Gap)
  );
}
